import { type ColumnDef } from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import type { RiskFlagRow } from "./types";
import {
  CategoryPill,
  StatusPill,
  PriorityPill,
  OverdueBadge,
  isOverdue,
} from "./pills";
import { UserNameColumn } from "@/components/users/user-name-column";
import { getRecipient } from "./utils";

export function createColumns(expandedGroups: Set<string>): ColumnDef<RiskFlagRow>[] {
  return [
  {
    id: "expand",
    header: () => null,
    size: 32,
    cell: ({ row }) => {
      const data = row.original;
      if (data._type !== "group") return null;
      const hasMultiple = data.group.alertCount > 1;
      if (!hasMultiple) return null;
      return expandedGroups.has(data.group.groupKey) ? (
        <ChevronDown className="h-4 w-4 text-zinc-400" />
      ) : (
        <ChevronRight className="h-4 w-4 text-zinc-400" />
      );
    },
  },
  {
    id: "user",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="User"
        className="justify-start"
      />
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const data = row.original;
      const alert =
        data._type === "group" ? data.group.latestAlert : data.alert;
      return (
        <UserNameColumn
          profilePhoto={alert.user?.profile_picture_url ?? ""}
          name={alert.user?.name ?? "Unknown"}
          researchConsent={false}
          redactedFields={alert.user?.redacted_fields}
          userId={alert.user?.id}
        />
      );
    },
  },
  {
    id: "recipient",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Recipient"
        className="justify-start"
      />
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const data = row.original;
      const alert =
        data._type === "group" ? data.group.latestAlert : data.alert;
      if (alert.sourceType === "survey") {
        return <span className="text-sm italic text-zinc-400">N/A – Survey</span>;
      }
      const recipient = getRecipient(alert);
      return (
        <UserNameColumn
          profilePhoto={recipient?.profile_picture_url ?? ""}
          name={recipient?.name ?? "Unknown"}
          researchConsent={false}
          redactedFields={recipient?.redacted_fields}
          userId={recipient?.id}
        />
      );
    },
  },
  {
    id: "time",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Time"
        className="justify-start"
      />
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const data = row.original;
      const alert =
        data._type === "group" ? data.group.latestAlert : data.alert;
      const overdue = isOverdue(
        alert.created_at,
        alert.priority,
        alert.status,
      );
      return (
        <div className="flex flex-col gap-0.5">
          {overdue && <OverdueBadge />}
          <span className="w-[140px] whitespace-nowrap text-sm text-zinc-500">
            {formatDistanceToNow(new Date(alert.created_at), {
              addSuffix: true,
            }).replace(/^about /, "")}
          </span>
        </div>
      );
    },
  },
  {
    id: "category",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Category"
        className="justify-start"
      />
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const data = row.original;
      if (data._type === "group") {
        const categories = [
          ...new Set(
            data.group.alerts.flatMap(
              (a) => a.categories?.map((c) => c.category) ?? [],
            ),
          ),
        ];
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            {categories.map((cat) => (
              <CategoryPill key={cat} category={cat} />
            ))}
          </div>
        );
      }
      return (
        <CategoryPill
          category={data.alert.categories?.[0]?.category ?? ""}
        />
      );
    },
  },
  {
    id: "status",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Status"
        className="justify-start"
      />
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const data = row.original;
      if (data._type !== "group") return null;
      const hasMultiple = data.group.alertCount > 1;
      return (
        <div className="flex items-center gap-2">
          <StatusPill
            status={data.group.latestAlert.status}
            resolutionReason={data.group.latestAlert.resolutionReason}
          />
          {hasMultiple && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-zinc-800 px-1.5 text-[11px] font-semibold text-white">
              {data.group.alertCount}
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: "tier",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Tier"
        className="justify-start"
      />
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const data = row.original;
      if (data._type === "group") {
        return <PriorityPill priority={data.group.priority} />;
      }
      return <PriorityPill priority={data.alert.priority} />;
    },
  },
];
}
